import { KeyRound, Loader2, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Field } from '@/components/common/FormBits'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { ACCESS_DURATIONS, useAccessLookup, useCreateAccessRequest } from '../access'
import { toastError } from '../ui'

const REASON_MIN = 10

type Props = {
  onClose: () => void
  /** Müşteri belliyse (ör. erişilemeyen müşteri sayfası) arama atlanır. */
  accountId?: string
  accountTitle?: string
}

/** Kapsam dışı müşteriye süreli erişim talebi: müşteri, zorunlu gerekçe ve süre. */
export function AccessRequestDialog({ onClose, accountId, accountTitle }: Props) {
  const { t } = useTranslation()
  const create = useCreateAccessRequest()
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [picked, setPicked] = useState<{ id: string; title: string } | null>(accountId ? { id: accountId, title: accountTitle ?? '' } : null)
  const [reason, setReason] = useState('')
  const [duration, setDuration] = useState<string>('1440')
  const [touched, setTouched] = useState(false)
  const lookup = useAccessLookup(debounced)

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), 250)
    return () => window.clearTimeout(id)
  }, [query])

  const reasonOk = reason.trim().length >= REASON_MIN
  const submit = () => {
    setTouched(true)
    if (!picked || !reasonOk) return
    create.mutate(
      { accountId: picked.id, reason, durationMinutes: Number(duration) },
      {
        onSuccess: () => {
          toast.success(t('crm.access.requested'))
          onClose()
        },
        onError: toastError,
      },
    )
  }

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onClose()}
      dismissible={!create.isPending}
      icon={<KeyRound />}
      title={t('crm.access.requestTitle')}
      description={t('crm.access.requestHint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('crm.access.send')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {accountId ? (
          <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
            <span className="text-muted-foreground">{t('crm.access.customer')}: </span>
            <span className="font-medium">{accountTitle || t('crm.access.thisCustomer')}</span>
          </div>
        ) : (
          <Field id="access-search" label={t('crm.access.customer')} error={touched && !picked ? t('crm.access.pickCustomer') : undefined} hint={t('crm.access.searchHint')}>
            <div className="space-y-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input id="access-search" className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('crm.access.searchPlaceholder')} autoComplete="off" />
              </div>
              {debounced.length >= 3 ? (
                <ul className="max-h-48 divide-y overflow-y-auto rounded-lg border" role="listbox" aria-label={t('crm.access.customer')}>
                  {lookup.isLoading ? (
                    <li className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</li>
                  ) : !lookup.data?.length ? (
                    <li className="px-3 py-2 text-sm text-muted-foreground">{t('crm.access.noResults')}</li>
                  ) : (
                    lookup.data.map((it) => {
                      const selected = picked?.id === it.accountId
                      const pending = !!it.pendingRequestId
                      return (
                        <li key={it.accountId}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            disabled={pending}
                            onClick={() => setPicked({ id: it.accountId, title: it.title })}
                            className={cn(
                              'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                              selected ? 'bg-primary/10 font-medium text-primary' : 'hover:bg-muted',
                            )}
                          >
                            <span className="truncate">{it.title}</span>
                            {pending ? <Badge tone="warning">{t('crm.access.alreadyPending')}</Badge> : null}
                          </button>
                        </li>
                      )
                    })
                  )}
                </ul>
              ) : null}
            </div>
          </Field>
        )}
        <Field id="access-reason" label={t('crm.access.reason')} error={touched && !reasonOk ? t('crm.access.reasonRequired') : undefined} full>
          <Textarea
            id="access-reason"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('crm.access.reasonPlaceholder')}
            aria-invalid={touched && !reasonOk}
          />
        </Field>
        <Field id="access-duration" label={t('crm.access.duration')} hint={t('crm.access.durationHint')}>
          <Select
            id="access-duration"
            value={duration}
            onChange={setDuration}
            options={ACCESS_DURATIONS.map((m) => ({ value: String(m), label: t(`crm.access.durations.${m}`) }))}
          />
        </Field>
      </div>
    </Modal>
  )
}
